import { Alert, Button, Card, DatePicker, Descriptions, Form, Space, message } from 'antd';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import { useState } from 'react';
import type { ReactNode } from 'react';

import { ApiError } from '../../api/errors';
import { updateProjectSchedule } from '../../api/projects';
import type { ProjectDetail } from '../../api/projects';
import { formatDate } from './projectDisplay';

/** Giá trị của biểu mẫu sửa lịch trình. */
interface ScheduleFormValues {
  readonly plannedStartDate: Dayjs | null;
  readonly plannedEndDate: Dayjs | null;
  readonly contractCompletionDate: Dayjs | null;
  readonly extendedCompletionDate: Dayjs | null;
  readonly actualStartDate: Dayjs | null;
  readonly actualEndDate: Dayjs | null;
}

/**
 * Thẻ lịch trình dự án.
 *
 * <b>Ba mốc hoàn thành được đặt cạnh nhau kèm giải thích ngắn</b>, vì đây là chỗ dễ lẫn nhất của cả
 * module: kế hoạch nội bộ thường có đệm, ngày cam kết trong hợp đồng là cơ sở tính phạt chậm tiến
 * độ, còn ngày gia hạn là sau phụ lục. Người điền nhầm ngày cam kết thành ngày kế hoạch sẽ làm mọi
 * báo cáo chậm tiến độ sai theo, và không có gì báo lỗi.
 *
 * Ở chế độ chỉ đọc vẫn hiện đủ ba mốc: người không có quyền sửa lịch vẫn cần biết hạn là ngày nào.
 */
export function ProjectScheduleCard({
  project,
  canEdit,
  onUpdated,
}: {
  readonly project: ProjectDetail;
  readonly canEdit: boolean;
  readonly onUpdated: (updated: ProjectDetail) => void;
}): ReactNode {
  const [form] = Form.useForm<ScheduleFormValues>();
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  function openEdit(): void {
    form.setFieldsValue({
      plannedStartDate: toDayjs(project.plannedStartDate),
      plannedEndDate: toDayjs(project.plannedEndDate),
      contractCompletionDate: toDayjs(project.contractCompletionDate),
      extendedCompletionDate: toDayjs(project.extendedCompletionDate),
      actualStartDate: toDayjs(project.actualStartDate),
      actualEndDate: toDayjs(project.actualEndDate),
    });

    setIsEditing(true);
  }

  async function submit(values: ScheduleFormValues): Promise<void> {
    setIsSaving(true);

    try {
      const updated = await updateProjectSchedule(project.id, {
        plannedStartDate: toIsoDate(values.plannedStartDate),
        plannedEndDate: toIsoDate(values.plannedEndDate),
        contractCompletionDate: toIsoDate(values.contractCompletionDate),
        extendedCompletionDate: toIsoDate(values.extendedCompletionDate),
        actualStartDate: toIsoDate(values.actualStartDate),
        actualEndDate: toIsoDate(values.actualEndDate),
      });

      onUpdated(updated);
      setIsEditing(false);
    } catch (error) {
      message.error(error instanceof ApiError ? error.message : 'Không lưu được lịch trình.');
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <Card
      size="small"
      title="Lịch trình"
      extra={
        isEditing ? (
          <Space>
            <Button size="small" onClick={() => { setIsEditing(false); }}>
              Hủy
            </Button>
            <Button size="small" type="primary" loading={isSaving} onClick={() => { form.submit(); }}>
              Lưu
            </Button>
          </Space>
        ) : (
          <Button size="small" disabled={!canEdit} onClick={openEdit}>
            Sửa lịch trình
          </Button>
        )
      }
    >
      {isEditing ? (
        <Form<ScheduleFormValues> form={form} layout="vertical" onFinish={(values) => void submit(values)}>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="Ba mốc hoàn thành khác nhau — đừng lẫn"
            description="Kế hoạch nội bộ thường đặt sớm hơn để có đệm. Ngày cam kết trong hợp đồng mới là cơ sở tính phạt chậm tiến độ. Ngày gia hạn là sau khi ký phụ lục, và phải muộn hơn ngày cam kết."
          />
          <Space wrap size="large" align="start">
            <Form.Item name="plannedStartDate" label="Bắt đầu theo kế hoạch">
              <DatePicker format="DD/MM/YYYY" allowClear />
            </Form.Item>
            <Form.Item name="plannedEndDate" label="Kết thúc theo kế hoạch">
              <DatePicker format="DD/MM/YYYY" allowClear />
            </Form.Item>
            <Form.Item name="contractCompletionDate" label="Cam kết trong hợp đồng">
              <DatePicker format="DD/MM/YYYY" allowClear />
            </Form.Item>
            <Form.Item
              name="extendedCompletionDate"
              label="Sau khi gia hạn"
              extra="Phải muộn hơn ngày cam kết."
            >
              <DatePicker format="DD/MM/YYYY" allowClear />
            </Form.Item>
            <Form.Item name="actualStartDate" label="Bắt đầu thực tế">
              <DatePicker format="DD/MM/YYYY" allowClear />
            </Form.Item>
            <Form.Item
              name="actualEndDate"
              label="Kết thúc thực tế"
              extra="Cần có ngày bắt đầu thực tế trước."
            >
              <DatePicker format="DD/MM/YYYY" allowClear />
            </Form.Item>
          </Space>
        </Form>
      ) : (
        <Descriptions size="small" column={{ xs: 1, sm: 2, lg: 3 }} bordered>
          <Descriptions.Item label="Bắt đầu theo kế hoạch">
            {formatDate(project.plannedStartDate)}
          </Descriptions.Item>
          <Descriptions.Item label="Kết thúc theo kế hoạch">
            {formatDate(project.plannedEndDate)}
          </Descriptions.Item>
          <Descriptions.Item label="Cam kết trong hợp đồng">
            {formatDate(project.contractCompletionDate)}
          </Descriptions.Item>
          <Descriptions.Item label="Sau khi gia hạn">
            {formatDate(project.extendedCompletionDate)}
          </Descriptions.Item>
          <Descriptions.Item label="Hạn đang có hiệu lực">
            {formatDate(project.committedCompletionDate)}
          </Descriptions.Item>
          <Descriptions.Item label="Thực tế">
            {formatDate(project.actualStartDate)} → {formatDate(project.actualEndDate)}
          </Descriptions.Item>
        </Descriptions>
      )}
    </Card>
  );
}

/**
 * Chuyển một ngày do máy chủ trả về sang đối tượng ngày của bộ chọn.
 *
 * Dựng từ chuỗi năm-tháng-ngày thuần, không kèm giờ và không kèm múi giờ, để bộ chọn hiển thị đúng
 * ngày ở mọi múi giờ của trình duyệt.
 */
function toDayjs(value: string | null): Dayjs | null {
  return value === null ? null : dayjs(value.slice(0, 10), 'YYYY-MM-DD');
}

/** Chuyển giá trị của bộ chọn về dạng chuỗi mà máy chủ nhận. */
function toIsoDate(value: Dayjs | null | undefined): string | null {
  return value === null || value === undefined ? null : value.format('YYYY-MM-DD');
}
